import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DollarSign, Package, Plus } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import {
  calcNetProfit,
  calcProfitMargin,
  useApp,
  type Sale,
  type SaleVenue,
} from "@/context/AppContext";

export const Route = createFileRoute("/sales/")({
  component: SalesPage,
});

const VENUES: (SaleVenue | "All")[] = [
  "All",
  "eBay",
  "Facebook Marketplace",
  "Local",
  "TCGplayer",
  "Other",
];

type StatusFilter =
  | "All"
  | "Pending"
  | "Picked"
  | "Packed"
  | "Shipped"
  | "Cancelled";

const STATUS_FILTERS: StatusFilter[] = [
  "All",
  "Pending",
  "Picked",
  "Packed",
  "Shipped",
  "Cancelled",
];

function saleStatus(
  sale: Sale,
): "Sold" | "Picked" | "Packed" | "Shipped" | "Cancelled" {
  // Cancellation supersedes every other lifecycle flag — a cancelled sale
  // shouldn't render as Pending/Picked/Packed just because those earlier
  // flags were set before the cancel landed.
  if (sale.cancelledAt) return "Cancelled";
  if (sale.shipped) return "Shipped";
  if (sale.packed) return "Packed";
  if (sale.pickedAt) return "Picked";
  return "Sold";
}

function SalesPage() {
  const navigate = useNavigate();
  const { sales } = useApp();
  const [filterVenue, setFilterVenue] = useState<SaleVenue | "All">("All");
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("All");

  const filtered = useMemo(() => {
    let result = [...sales];
    if (filterVenue !== "All")
      result = result.filter((s) => s.venue === filterVenue);
    if (filterStatus !== "All") {
      result = result.filter((s) => {
        const status = saleStatus(s);
        if (filterStatus === "Pending") return status === "Sold";
        return status === filterStatus;
      });
    }
    return result.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [sales, filterVenue, filterStatus]);

  const totals = useMemo(() => {
    const gross = sales.reduce((s, sl) => s + sl.salePrice, 0);
    const net = sales.reduce((s, sl) => s + calcNetProfit(sl), 0);
    const fees = sales.reduce(
      (s, sl) => s + sl.platformFees + sl.paymentFees + sl.actualShipping,
      0,
    );
    return { gross, net, fees };
  }, [sales]);

  return (
    <section className="flex h-full flex-col">
      <div className="space-y-3 border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">Sales</h1>
          <button
            type="button"
            onClick={() => navigate({ to: "/sales/create" })}
            aria-label="Log Sale"
            className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-accent-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <StatBox label="Gross Sales" value={`$${totals.gross.toFixed(2)}`} />
          <StatBox
            label="Net Profit"
            value={`$${totals.net.toFixed(2)}`}
            tone={
              totals.net > 0
                ? "positive"
                : totals.net < 0
                  ? "negative"
                  : undefined
            }
          />
          <StatBox
            label="Total Fees"
            value={`$${totals.fees.toFixed(2)}`}
            tone="negative"
          />
        </div>

        <div className="flex border-b border-border">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilterStatus(s)}
              className={`relative -mb-px border-b-2 px-3 py-1.5 text-xs font-semibold ${
                filterStatus === s
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {VENUES.map((venue) => (
            <button
              key={venue}
              type="button"
              onClick={() => setFilterVenue(venue)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                filterVenue === venue
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {venue}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {filtered.length === 0 ? (
          <EmptyState
            icon={<DollarSign className="h-7 w-7" />}
            title="No sales yet"
            description="Log your first sale to start tracking profit"
            actionLabel="Log Sale"
            onAction={() => navigate({ to: "/sales/create" })}
          />
        ) : (
          <div className="space-y-2">
            {filtered.map((sale) => {
              const net = calcNetProfit(sale);
              const margin = calcProfitMargin(sale);
              const status = saleStatus(sale);
              return (
                <button
                  key={sale.id}
                  type="button"
                  onClick={() =>
                    navigate({
                      to: "/sales/$id",
                      params: { id: sale.id },
                    })
                  }
                  className="block w-full rounded-md border border-border bg-card p-3.5 text-left shadow-md transition-shadow hover:border-primary/50 hover:shadow-lg"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                        {sale.id}
                      </div>
                      <div className="truncate text-[15px] font-semibold text-foreground">
                        {sale.itemName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatSaleDate(sale.saleDate)} · {sale.venue}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-lg font-bold text-foreground">
                        ${sale.salePrice.toFixed(2)}
                      </div>
                      <div
                        className={`text-xs font-semibold ${
                          net >= 0 ? "text-emerald-600" : "text-destructive"
                        }`}
                      >
                        ${net.toFixed(2)} net
                      </div>
                    </div>
                  </div>
                  <div className="my-2.5 h-px bg-border" />
                  {sale.suppliesBreakdown &&
                    sale.suppliesBreakdown.length > 0 && (
                      <div className="mb-2 flex items-center gap-1.5">
                        <Package className="h-3 w-3 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                          {sale.suppliesBreakdown.map((s) => s.name).join(", ")}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          $
                          {sale.suppliesBreakdown
                            .reduce((sum, s) => sum + s.unitCost, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Margin: {margin.toFixed(1)}%</span>
                    <span>
                      Fees: ${(sale.platformFees + sale.paymentFees).toFixed(2)}
                    </span>
                    <span className="flex items-center gap-1">
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          status === "Cancelled"
                            ? "bg-destructive"
                            : status === "Shipped"
                              ? "bg-emerald-500"
                              : status === "Packed"
                                ? "bg-sky-500"
                                : status === "Picked"
                                  ? "bg-indigo-500"
                                  : "bg-amber-500"
                        }`}
                      />
                      {status === "Sold" ? "Pending" : status}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function formatSaleDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

function StatBox({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
}) {
  const valueClass =
    tone === "positive"
      ? "text-emerald-600"
      : tone === "negative"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2">
      <div className={`text-base font-bold ${valueClass}`}>{value}</div>
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
